@file:Suppress("UnsafeCastFromDynamic")

package com.zi.support.web

import com.zi.support.shared.CallTicketPolicy
import com.zi.support.shared.SearchableSupportCall
import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement

internal class WebSupportCall(
    val id: String,
    val callerName: String,
    val callerPhone: String,
    val startedAt: dynamic,
    val startedAtMillis: Double,
    val outcome: String,
    val durationSeconds: Long?,
    val receivedByUid: String,
    val receivedByName: String,
    val deviceName: String,
    val direction: String = "incoming",
    val linkedTicketId: String = "",
    val linkedTicketNumber: String = "",
    val linkedTicketPath: String = "",
    val personal: Boolean = false,
    val spam: Boolean = false,
) {
    val callerLabel: String get() = callerName.ifBlank { "Unknown caller" }
    val needsTicket: Boolean get() = com.zi.support.shared.CallLogPolicy.needsTicket(linkedTicketId, personal)

    companion object {
        fun from(document: dynamic): WebSupportCall {
            val data = document.data()
            return WebSupportCall(
                id = document.id as String,
                callerName = field(data, "callerName"),
                callerPhone = field(data, "callerPhone"),
                startedAt = data.startedAt,
                startedAtMillis = timestampMillis(data.startedAt),
                outcome = field(data, "outcome").ifBlank { "unknown" },
                durationSeconds = (data.durationSeconds as? Number)?.toLong(),
                receivedByUid = field(data, "receivedByUid"),
                receivedByName = field(data, "receivedByName"),
                deviceName = field(data, "deviceName"),
                direction = field(data, "direction").ifBlank { "incoming" },
                linkedTicketId = field(data, "linkedTicketId"),
                linkedTicketNumber = field(data, "linkedTicketNumber"),
                linkedTicketPath = field(data, "linkedTicketPath"),
                personal = data.personal == true,
                spam = data.spam == true,
            )
        }
    }

    fun matches(search: String): Boolean = CallTicketPolicy.matchesSearch(
        SearchableSupportCall(callerName, callerPhone, receivedByName, deviceName),
        search,
    )
}

internal enum class WebCallScope { MY, TEAM }

/** A deliberately lazy, one-shot picker. Nothing queries supportCalls until [show] is called. */
internal object SupportCalls {
    private var scope = WebCallScope.MY
    private var rows: List<WebSupportCall> = emptyList()
    private var onSelected: ((WebSupportCall) -> Unit)? = null

    fun show(selected: (WebSupportCall) -> Unit) {
        close()
        scope = WebCallScope.MY
        rows = emptyList()
        onSelected = selected
        val node = document.createElement("div") as HTMLElement
        node.id = "call-picker-root"
        node.className = "call-picker-backdrop"
        node.innerHTML = """
          <section class="call-picker" role="dialog" aria-modal="true" aria-labelledby="call-picker-title">
            <header class="call-picker-head"><div><p class="eyebrow">Optional ticket source</p><h2 id="call-picker-title">Link a call</h2></div><button id="call-picker-close" class="btn btn-icon btn-ghost" aria-label="Close">✕</button></header>
            <div class="call-picker-body">
              <div class="segmented call-scope" role="group" aria-label="Calls to show"><button class="active" data-call-scope="my">My calls</button><button data-call-scope="team">Team calls</button></div>
              <div class="search-shell call-search-shell"><span class="material-symbols-rounded" aria-hidden="true">search</span><label class="visually-hidden" for="call-search">Search calls</label><input id="call-search" class="input search" placeholder="Search caller, receiver, or device"></div>
              <div id="call-picker-status" class="call-picker-status" aria-live="polite">Loading calls…</div>
              <div id="call-picker-list" class="call-picker-list"></div>
            </div>
          </section>
        """.trimIndent()
        document.body?.appendChild(node)
        click("call-picker-close") { close() }
        node.addEventListener("keydown", { event -> if (event.asDynamic().key == "Escape") close() })
        type("call-search") { renderRows() }
        document.querySelectorAll("[data-call-scope]").let { buttons ->
            for (index in 0 until buttons.length) buttons.item(index)?.addEventListener("click", { event ->
                val button = event.currentTarget as? HTMLElement ?: return@addEventListener
                scope = if (button.getAttribute("data-call-scope") == "team") WebCallScope.TEAM else WebCallScope.MY
                for (buttonIndex in 0 until buttons.length) (buttons.item(buttonIndex) as? HTMLElement)?.classList?.remove("active")
                button.classList.add("active")
                load()
            })
        }
        load()
    }

    fun close() {
        document.getElementById("call-picker-root")?.remove()
        onSelected = null
    }

    fun summary(call: WebSupportCall): String {
        val duration = call.durationSeconds?.let { " · ${it / 60}m ${it % 60}s" }.orEmpty()
        return """
          <div class="linked-call-main"><span class="material-symbols-rounded" aria-hidden="true">call</span><div><strong>${escape(call.callerLabel)}</strong>${call.callerPhone.takeIf(String::isNotBlank)?.let { "<small>${escape(it)}</small>" }.orEmpty()}</div></div>
          <p>${escape(formatDate(call.startedAt))} · ${escape(call.outcome.replaceFirstChar { it.uppercase() })}$duration</p>
          <p>Received by ${escape(call.receivedByName.ifBlank { "Support user" })} · ${escape(call.deviceName.ifBlank { "Unknown device" })}</p>
        """.trimIndent()
    }

    private fun load() {
        val uid = FirebaseGateway.currentUser()?.uid as? String
        if (uid.isNullOrBlank()) { renderError("Sign in to view calls."); return }
        element("call-picker-status")?.textContent = "Loading calls…"
        element("call-picker-list")?.innerHTML = "<div class=\"call-picker-loading\"><span class=\"spinner-mini\"></span></div>"
        val fs = FirebaseGateway.firestore
        val constraints = mutableListOf<dynamic>()
        constraints.add(fs.where("linkedTicketId", "==", null))
        if (scope == WebCallScope.MY) constraints.add(fs.where("receivedByUid", "==", uid))
        constraints.add(fs.orderBy("startedAt", "desc"))
        constraints.add(fs.limit(100))
        promise(FirebaseGateway.getCollection("supportCalls", constraints.toTypedArray()), { snapshot ->
            val loaded = mutableListOf<WebSupportCall>()
            val documents = snapshot.docs
            // Personal calls can never get a ticket, so the picker leaves them out.
            for (index in 0 until (documents.length as Int)) WebSupportCall.from(documents[index]).takeUnless { it.personal || it.spam }?.let { loaded += it }
            rows = loaded.sortedByDescending { it.startedAtMillis }
            renderRows()
        }, ::renderError)
    }

    private fun renderRows() {
        val root = element("call-picker-list") ?: return
        val visible = rows.filter { it.matches(input("call-search")) }
        element("call-picker-status")?.textContent = if (visible.isEmpty()) {
            "No unlinked calls match this view."
        } else {
            "${visible.size} recent unlinked call${if (visible.size == 1) "" else "s"}"
        }
        root.innerHTML = visible.joinToString("") { call ->
            val duration = call.durationSeconds?.let { " · ${it / 60}m ${it % 60}s" }.orEmpty()
            val receiver = if (scope == WebCallScope.TEAM) "<small>Received by ${escape(call.receivedByName.ifBlank { "Support user" })} · ${escape(call.deviceName.ifBlank { "Unknown device" })}</small>" else ""
            """<button type="button" class="call-result" data-support-call="${escape(call.id)}"><span class="call-result-icon material-symbols-rounded" aria-hidden="true">phone_in_talk</span><span><strong>${escape(call.callerLabel)}</strong>${call.callerPhone.takeIf(String::isNotBlank)?.let { "<small>${escape(it)}</small>" }.orEmpty()}<small>${escape(formatDate(call.startedAt))} · ${escape(call.outcome.replaceFirstChar { it.uppercase() })}$duration</small>$receiver</span><span class="material-symbols-rounded" aria-hidden="true">arrow_forward</span></button>"""
        }
        document.querySelectorAll("[data-support-call]").let { nodes ->
            for (index in 0 until nodes.length) nodes.item(index)?.addEventListener("click", { event ->
                val id = (event.currentTarget as? HTMLElement)?.getAttribute("data-support-call").orEmpty()
                rows.firstOrNull { it.id == id }?.let(::askPurpose)
            })
        }
    }

    private fun askPurpose(call: WebSupportCall) {
        element("call-picker-status")?.textContent = call.callerLabel
        element("call-picker-list")?.innerHTML = "<div class=\"call-picker-error\"><span class=\"material-symbols-rounded\">help</span><p><strong>Is this call personal, work or spam?</strong><br>Only work calls can be linked to a ticket.</p><div><button id=\"call-purpose-work\" class=\"btn btn-primary\">Work</button> <button id=\"call-purpose-personal\" class=\"btn\">Personal</button> ${if (call.callerPhone.isBlank()) "" else "<button id=\"call-purpose-spam\" class=\"btn\">Spam</button> "}<button id=\"call-purpose-back\" class=\"btn btn-ghost\">Back</button></div></div>"
        click("call-purpose-work") { val callback = onSelected; close(); callback?.invoke(call) }
        click("call-purpose-back") { renderRows() }
        click("call-purpose-spam") { CallsScreen.markSpam(call.id); rows = rows.filterNot { it.callerPhone == call.callerPhone }; renderRows() }
        click("call-purpose-personal") {
            CallsScreen.setPersonal(call.id, true); rows = rows.filterNot { it.id == call.id }
            element("call-picker-list")?.innerHTML = "<div class=\"call-picker-error\"><span class=\"material-symbols-rounded\">block</span><p>A ticket can't be created for a personal call.</p><button id=\"call-purpose-back\" class=\"btn\">Back to calls</button></div>"
            click("call-purpose-back") { renderRows() }
        }
    }

    private fun renderError(message: String) {
        element("call-picker-status")?.textContent = "Calls could not be loaded."
        element("call-picker-list")?.innerHTML = "<div class=\"call-picker-error\"><span class=\"material-symbols-rounded\">phone_disabled</span><p>${escape(message)}</p><button id=\"retry-calls\" class=\"btn\">Retry</button></div>"
        click("retry-calls") { load() }
    }
}
